/**
 * ModalImportarAsistencia.js
 *
 * Confirma la importación de incidencias desde el reloj checador y deja elegir qué
 * días del periodo se importan: todo, hasta un día, o sólo algunos.
 *
 * Existe porque el reporte de asistencia marca falta en todo día con horario y sin
 * checada, también en los que todavía no pasan: importar a media semana llenaba de
 * faltas falsas el resto del periodo. Por eso, si el periodo sigue corriendo, el modal
 * abre en "hasta ayer".
 *
 * Los días que no se eligen no se tocan en el servidor: ni se escriben ni se limpian.
 */

import React, { Component } from 'react'
import { Modal, Radio, Select, Checkbox, Button } from 'antd'
import dayjs from 'dayjs'

import { anclarDesdeUtc, construirDiasPeriodo } from '../incidenciasListaRaya'

const { Option } = Select

const MS_DIA = 86400000

const rotuloDia = d => `${d.etiqueta} ${d.diaMes}`

class ModalImportarAsistencia extends Component {
  state = {
    modo: 'todo', // 'todo' | 'hasta' | 'algunos'
    hastaDia: 1,
    diasElegidos: [],
    importando: false,
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.visible && this.props.visible) this.reset()
  }

  reset() {
    const dias = construirDiasPeriodo(this.props.prenomina)
    const total = dias.length

    // Posición de hoy dentro del periodo, con la fecha local (México) anclada igual que
    // las del periodo para que la resta dé días enteros.
    const ahora = new Date()
    const hoy = Date.UTC(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), 12)
    const inicio = anclarDesdeUtc(this.props.prenomina?.fechaInicio)
    const posHoy = inicio === null ? Infinity : Math.round((hoy - inicio) / MS_DIA) + 1

    // Periodo ya terminado: todo. Si sigue corriendo: hasta ayer (o el primer día si hoy
    // es el primero o el periodo aún no empieza).
    const periodoTerminado = !total || posHoy > total
    this.setState({
      modo: periodoTerminado ? 'todo' : 'hasta',
      hastaDia: periodoTerminado ? total : Math.min(Math.max(posHoy - 1, 1), total),
      diasElegidos: [],
      importando: false,
    })
  }

  // undefined = periodo completo: así el servidor no recibe el parámetro y hace lo de siempre.
  diasPeriodoPayload() {
    const { modo, hastaDia, diasElegidos } = this.state
    if (modo === 'hasta') return Array.from({ length: hastaDia }, (_, i) => i + 1)
    if (modo === 'algunos') return [...diasElegidos].sort((a, b) => a - b)
    return undefined
  }

  async importar() {
    const diasPeriodo = this.diasPeriodoPayload()
    this.setState({ importando: true })
    try {
      await this.props.onImportar(diasPeriodo ? { diasPeriodo } : {})
    } finally {
      this.setState({ importando: false })
    }
    this.props.onCerrar()
  }

  renderSelector(dias) {
    const { modo, hastaDia, diasElegidos } = this.state

    return (
      <Radio.Group
        value={modo}
        onChange={e => this.setState({ modo: e.target.value })}
        style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}
      >
        <Radio value="todo">Todo el periodo</Radio>

        {/* El Select va fuera del Radio: dentro de su <label>, el clic se lo come el radio
            y el desplegable se cierra solo. */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Radio value="hasta">Hasta el día</Radio>
          <Select
            size="small"
            value={hastaDia}
            onChange={v => this.setState({ hastaDia: v, modo: 'hasta' })}
            style={{ width: 110 }}
          >
            {dias.map(d => (
              <Option key={d.diaPeriodo} value={d.diaPeriodo}>{rotuloDia(d)}</Option>
            ))}
          </Select>
        </div>

        <Radio value="algunos">Sólo algunos días</Radio>
        {modo === 'algunos' && (
          <div style={{ marginLeft: 24 }}>
            <div style={{ marginBottom: 6 }}>
              <Button type="link" size="small" style={{ padding: 0 }}
                onClick={() => this.setState({ diasElegidos: dias.map(d => d.diaPeriodo) })}>
                Todos
              </Button>
              {' · '}
              <Button type="link" size="small" style={{ padding: 0 }}
                onClick={() => this.setState({ diasElegidos: [] })}>
                Ninguno
              </Button>
            </div>
            <Checkbox.Group
              value={diasElegidos}
              onChange={v => this.setState({ diasElegidos: v })}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 4 }}
            >
              {dias.map(d => (
                <Checkbox key={d.diaPeriodo} value={d.diaPeriodo} style={{ marginLeft: 0 }}>
                  {rotuloDia(d)}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        )}
      </Radio.Group>
    )
  }

  render() {
    const { visible, prenomina, onCerrar } = this.props
    const { modo, diasElegidos, importando } = this.state
    if (!prenomina) return null

    const dias = construirDiasPeriodo(prenomina)
    const sinDias = modo === 'algunos' && !diasElegidos.length

    return (
      <Modal
        title="Importar incidencias de asistencia"
        visible={visible}
        onCancel={onCerrar}
        onOk={() => this.importar()}
        confirmLoading={importando}
        okButtonProps={{ disabled: sinDias }}
        okText="Importar"
        width={560}
      >
        <p>
          Se calculará la asistencia del periodo del{' '}
          <b>{dayjs(prenomina.fechaInicio).format('DD/MM/YYYY')}</b> al{' '}
          <b>{dayjs(prenomina.fechaFin).format('DD/MM/YYYY')}</b>. Sólo se importarán
          los empleados de esta prenómina que tengan una asignación vigente a un reloj
          checador; al resto no se le tocará ninguna incidencia. Se crearán incidencias
          de Falta, Retardo, Salida Temprana o Tiempo Extra según lo detectado por el
          reloj checador y lo configurado en Configuración de Nómina.
        </p>

        <div style={{ fontWeight: 500, marginBottom: 8 }}>¿Qué días importar?</div>
        {this.renderSelector(dias)}

        <p style={{ color: '#888', fontSize: 12, marginBottom: 0 }}>
          Los días que no elijas se quedan como están. Los días que ya tengan una incidencia
          capturada manualmente no se modificarán. Los días importados anteriormente que ya no
          tengan ninguna incidencia detectada (p.ej. se corrigió la checada) se quitarán
          automáticamente.
        </p>
      </Modal>
    )
  }
}

export default ModalImportarAsistencia
